import React, { Component, useState, useEffect } from 'react';
import { CordType, DischargeType, ImpellerMaterial, LEPPumpViewModel, LowerSealMaterial, PumpProperty } from './common/LEPPumpViewModel';
import { Pricing } from '../pricing';
import { Button } from './common/Button';
import { PriceDisplay } from './common/PriceDisplay';

interface IProps {
    showAccessories?: () => void;
    viewModel?: LEPPumpViewModel;
    forceRender?: () => void;
    pricing: Pricing;
}

interface IState {}

/**
 * This component shows all choices made in the PumpChoiceSelector, as well as 
 * secondary configuration options available to that configured model.
 */
export class PumpDisplay extends Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);
        this.forceRender = this.forceRender.bind(this);
        this.state = {};
    }

    forceRender(): void {
        if (!this.props.forceRender) {
            return;
        }
        this.props.forceRender();
    }

    render() {
        if (!this.props.viewModel) {
            return <></>;
        }
        return (
            <div id='pump-display'>
                <div className='bap-row mb-2'>
                    <div className='bap-col-12 text-center'>
                    <h4>Select Features</h4> 
                    </div>
                </div>

                {/* Header with model number and pricing */}
                <div className='bap-row mb-2'>
                    <div className='bap-col-4'>
                        <div id='button-shrinker'>
                            {this.props.viewModel.complete() ? <Button cssClass='btn btn-sm btn-pump-choice' clicked={this.props.showAccessories}>Matching Accessories For This Model</Button> : null}
                        </div>
                    </div>
                    <div className='bap-col-4 text-center'>
                        <h4><small>Model:&nbsp;</small><label className='text-underline'>{this.props.viewModel?.getModelNumber()}</label></h4>
                    </div>
                    <div className='bap-col-4 text-right'>
                        {/* pricing */}
                        {this.props.viewModel.complete() ? <PriceDisplay pricing={this.props.pricing} modelNumber={this.props.viewModel.getModelNumber()} /> : null}
                    </div>
                </div>

                {/* Part number options */}
                <div className='bap-row'>
                    <PartOptionList selection={this.props.viewModel.solidsHandlingSize?.toString()} 
                                    labelText={'Solids Handling'} 
                                    options={this.props.viewModel.solidsHandlingSizeOptions}
                                    />
                    <PartOptionList selection={this.props.viewModel.pumpType} 
                                    labelText='Pump Type' 
                                    options={[{Code: this.props.viewModel.pumpType, Description: this.props.viewModel.pumpTypeDesc}]} 
                                    />
                    <PartOptionList selection={this.props.viewModel.horsepower} 
                                    labelText='Horsepower' 
                                    options={[{Code: this.props.viewModel.horsepower, Description: this.props.viewModel.horsepowerDesc}]} 
                                    />
                    <PartOptionList selection={this.props.viewModel.voltage?.toString()} 
                                    labelText='Voltage' 
                                    options={this.props.viewModel.voltageOptions} 
                                    optionClicked={(option: PumpProperty) => {
                                        if (!this.props.viewModel) {
                                            return;
                                        }
                                        // Set the voltage value on the viewModel based on the user's choice
                                        this.props.viewModel.voltage = parseInt(option.Code ? option.Code: '');
                                        this.forceRender();
                                    }}
                                    />
                    {this.props.viewModel.operationType ? <PartOptionList selection={this.props.viewModel.operationType} labelText='Pump Operation' options={[{Code: this.props.viewModel.operationType, Description: this.props.viewModel.operationTypeDesc}]}/> : null}
                    {this.props.viewModel.frequency ? <PartOptionList selection={this.props.viewModel.frequency?.toString()} labelText='Frequency Hz' options={this.props.viewModel.frequencyOptions}/> : null }
                    <PartOptionList selection={this.props.viewModel.impellerSize} 
                                    labelText='Impeller Size' 
                                    options={this.props.viewModel.impellerSizeOptions}
                                    optionClicked={(option: PumpProperty) => {
                                        if (!this.props.viewModel) {
                                            return;
                                        }
                                        // Set the impeller size on the viewModel based on the user's choice
                                        this.props.viewModel.impellerSize = option.Code;
                                        this.forceRender();
                                    }}
                                    />
                    <PartOptionList selection={this.props.viewModel.impellerMaterial} 
                                    labelText='Impeller Material' 
                                    options={this.props.viewModel.impellerMaterialOptions}
                                    optionClicked={(option: PumpProperty) => {
                                        if (!this.props.viewModel) {
                                            return;
                                        }
                                        // Set the impeller material on the viewModel based on the user's choice
                                        for (let item in ImpellerMaterial) {
                                            if (item === option.Code) {
                                                this.props.viewModel.impellerMaterial = item as Nullable<ImpellerMaterial>;
                                            }
                                        }
                                        this.forceRender();
                                    }}
                                    />
                    <PartOptionList selection={this.props.viewModel.sealConfig} 
                                    labelText='Seal Config' 
                                    options={this.props.viewModel.sealConfigOptions}
                                    optionClicked={(option: PumpProperty) => {
                                        if (!this.props.viewModel) {
                                            return;
                                        }
                                        // Set the seal configuration on the viewModel based on the user's choice
                                        this.props.viewModel.sealConfig = option.Code;
                                        this.forceRender();
                                    }}
                                    />
                    <PartOptionList selection={this.props.viewModel.lowerSealMaterial} 
                                    labelText='Lower Seal' 
                                    options={this.props.viewModel.lowerSealMaterialOptions}
                                    optionClicked={(option: PumpProperty) => {
                                        if (!this.props.viewModel) {
                                            return;
                                        }
                                        // Set the seal material on the viewModel based on the user's choice
                                        for (let item in LowerSealMaterial) {
                                            if (item === option.Code) {
                                                this.props.viewModel.lowerSealMaterial = item as Nullable<LowerSealMaterial>;
                                            }
                                        }
                                        this.forceRender();
                                    }}
                                    />
                    <PartOptionList selection={this.props.viewModel.discharge} 
                                    labelText='Discharge' 
                                    options={this.props.viewModel.dischargeOptions}
                                    optionClicked={(option: PumpProperty) => {
                                        if (!this.props.viewModel) {
                                            return;
                                        }
                                        // Set the discharge type on the viewModel based on the user's choice
                                        for (let item in DischargeType) {
                                            if (item === option.Code) {
                                                this.props.viewModel.discharge = item as Nullable<DischargeType>;
                                            }
                                        }
                                        this.forceRender();
                                    }}
                                    />
                    <PartOptionList selection={this.props.viewModel.cordType?.toString()} 
                                    labelText='Cord Type' 
                                    options={this.props.viewModel.cordTypeOptions}
                                    optionClicked={(option: PumpProperty) => {
                                        if (!this.props.viewModel) {
                                            return;
                                        }
                                        // Set the cord type on the viewModel based on the user's choice
                                        for (let item in CordType) {
                                            if (item === option.Code) {
                                                this.props.viewModel.cordType = item as Nullable<CordType>;
                                            }
                                        }
                                        this.forceRender();
                                    }}
                                    />
                    <PartOptionList selection={this.props.viewModel.dischargeOrientationDisplay()}
                                    labelText='Discharge Orientation'
                                    options={this.props.viewModel.dischargeOrientationOptions}
                                    optionClicked={(option : PumpProperty) => {
                                        if (!this.props.viewModel) {
                                            return;
                                        }
                                        // Set the discharge orientation on the viewModel based on the user's choice
                                        this.props.viewModel.verticalDischarge = option.Code === 'V';
                                        this.forceRender();
                                    }}
                                    />
                    <PartOptionList selection={this.props.viewModel.cordLength} 
                                    labelText='Cord Length' 
                                    options={this.props.viewModel.cordLengthOptions}
                                    optionClicked={(option: PumpProperty) => {
                                        if (!this.props.viewModel) {
                                            return;
                                        }
                                        // Set the cord length on the viewModel based on the user's choice
                                        this.props.viewModel.cordLength = option.Code;
                                        this.forceRender();
                                    }}
                                    />
                    <PartOptionList selection={this.props.viewModel.capacitorDisplay()} 
                                    labelText='Capacitor' 
                                    options={this.props.viewModel.isThreePhase ? [] : this.props.viewModel.capacitorOptions}
                                    optionClicked={(option: PumpProperty) => {
                                        if (!this.props.viewModel) {
                                            return;
                                        }
                                        // Set the capacitor on the viewModel based on the user's choice
                                        this.props.viewModel.capacitor = option.Code === 'C';
                                        this.forceRender();
                                    }}
                                    />
                </div>

                {/* Legend */}
                <div id='legend' className='bap-row'>
                    <div className='bap-col-1 standard'></div>
                    <div className='bap-col-2'>
                        <b>- Standard Build</b>
                    </div>
                    <div className='bap-col-1 selected'></div>
                    <div className='bap-col-2'>
                        <b>- Selected Item</b>
                    </div>
                </div>
            </div>
        );
    }
};

interface OptionProps {
    selection: Nullable<string> | undefined;
    labelText: string;
    options: PumpProperty[];
    optionClicked?: (option: PumpProperty) => void;
}

/**
 * The PartOptionList function component vertically displays a list of options 
 * from which you can select. It will notify its parent component when a selection
 * is made.
 */
const PartOptionList = (props: OptionProps) : JSX.Element => {
    const [selectedIndex, setSelectedIndex] = useState(-1);
    // used to reset the selected index when the options change.
    useEffect(() => {
        setSelectedIndex(-1);
    }, [props.options]);

    const getClassName = (option: PumpProperty, index: number) : string => {
        let cssClass: string = 'list-group-item';
        if (option.Standard) {
            cssClass += ' standard';
        }
        // if the model has a selected value that matches this option, or if 
        // the selected index equals this index, make it selected
        if ((selectedIndex >= 0 && selectedIndex === index) || (option.Code === props.selection)) {
            cssClass += ' selected';
        }
        return cssClass;
    }

    const optionClicked = (option: PumpProperty, index: number) : void => {
        if (props.optionClicked) {
            setSelectedIndex(index);
            props.optionClicked(option);
        }
    }

    if (props.options.length < 1) {
        return (
            <></>
        );
    }

    // if there is only one option, style it as if it's the standard option.
    if (props.options.length === 1) {
        props.options[0].Standard = true;
    }  

    return (
        <div className='bap-col-1 form-group'>
            <div>
                <span className='form-control'>{props.selection ? props.selection : null}</span>
            </div>
            <label>{props.labelText}</label>
            <ul className='list-group'>
               {props.options.map((option, index) => (
                <li key={index} 
                    onClick={() => optionClicked(option, index)} 
                    className={getClassName(option, index)}>
                        {option.Description}
                </li>
               ))} 
            </ul>
        </div>
    );
};
